import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function Store() {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cartItems, setCartItems] = useState<{[key: string]: number}>({});

  const store = {
    id: 1,
    name: 'بقالة الأمانة',
    rating: 4.8,
    distance: '0.5 كم',
    workingHours: '6:00 ص - 12:00 م',
    deliveryTime: '15-30 دقيقة',
    image: 'https://public.readdy.ai/ai/img_res/27dd4dc1c575347d133ad2e483a4c3df.jpg'
  };

  const categories = [
    { id: 'all', name: 'الكل' },
    { id: 'vegetables', name: 'خضروات' },
    { id: 'fruits', name: 'فواكه' },
    { id: 'dairy', name: 'ألبان' },
    { id: 'meat', name: 'لحوم' }
  ];

  const products = [
    {
      id: 1,
      name: 'طماطم طازجة',
      price: 8.50,
      originalPrice: 10.00,
      unit: 'كيلو',
      category: 'vegetables',
      image: 'https://readdy.ai/api/search-image?query=Fresh%20red%20tomatoes%2C%20high%20quality%20produce%20photography%2C%20clean%20white%20background%2C%20realistic%20food%20photography%2C%20vibrant%20colors&width=150&height=150&seq=tomato&orientation=squarish',
      inStock: true
    },
    {
      id: 2,
      name: 'تفاح أحمر',
      price: 12.00,
      unit: 'كيلو',
      category: 'fruits',
      image: 'https://readdy.ai/api/search-image?query=Fresh%20red%20apples%2C%20premium%20quality%20fruit%20photography%2C%20clean%20white%20background%2C%20realistic%20food%20photography%2C%20appetizing%20presentation&width=150&height=150&seq=apple&orientation=squarish',
      inStock: true
    },
    {
      id: 3,
      name: 'حليب طازج',
      price: 6.50,
      unit: 'لتر',
      category: 'dairy',
      image: 'https://readdy.ai/api/search-image?query=Fresh%20milk%20bottle%2C%20dairy%20product%20photography%2C%20clean%20white%20background%2C%20realistic%20product%20photography%2C%20premium%20quality&width=150&height=150&seq=milk&orientation=squarish',
      inStock: true
    },
    {
      id: 4,
      name: 'دجاج طازج',
      price: 25.00,
      unit: 'كيلو',
      category: 'meat',
      image: 'https://readdy.ai/api/search-image?query=Fresh%20chicken%20meat%2C%20butcher%20shop%20quality%2C%20clean%20presentation%2C%20realistic%20food%20photography%2C%20white%20background&width=150&height=150&seq=chicken&orientation=squarish',
      inStock: true
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  const addToCart = (productId: number) => {
    setCartItems(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1
    }));
  };

  const removeFromCart = (productId: number) => {
    setCartItems(prev => {
      const newItems = { ...prev };
      if (newItems[productId] > 1) {
        newItems[productId]--;
      } else {
        delete newItems[productId];
      }
      return newItems;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* الهيدر */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between p-4 pt-12">
          <button onClick={() => navigate(-1)} className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          <h1 className="text-lg font-bold">تفاصيل المتجر</h1>
          <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="ri-heart-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* معلومات المتجر */}
      <div className="bg-white mx-4 mt-4 rounded-lg shadow-sm overflow-hidden">
        <img
          src={store.image}
          alt={store.name}
          className="w-full h-40 object-cover"
        />
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-gray-800">{store.name}</h2>
            <div className="flex items-center">
              <i className="ri-star-fill text-yellow-400 ml-1"></i>
              <span className="text-gray-600">{store.rating}</span>
            </div>
          </div>
          <div className="flex items-center text-sm text-gray-500 space-x-4 space-x-reverse">
            <span className="flex items-center">
              <i className="ri-map-pin-line ml-1"></i>
              {store.distance}
            </span>
            <span className="flex items-center">
              <i className="ri-time-line ml-1"></i>
              {store.deliveryTime}
            </span>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            ساعات العمل: {store.workingHours}
          </div>
        </div>
      </div>

      {/* فئات المنتجات */}
      <div className="px-4 mt-6">
        <div className="flex space-x-2 space-x-reverse overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* قائمة المنتجات */}
      <div className="px-4 mt-6">
        <div className="grid grid-cols-2 gap-4">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-32 object-cover"
                />
                {product.originalPrice && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs">
                    خصم
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-medium text-gray-800 text-sm mb-1">{product.name}</h3>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-green-600 font-bold">{product.price} ر.س</span>
                    {product.originalPrice && (
                      <span className="text-gray-400 text-xs line-through mr-1">
                        {product.originalPrice} ر.س
                      </span>
                    )}
                    <div className="text-xs text-gray-500">/{product.unit}</div>
                  </div>
                </div>
                
                {cartItems[product.id] ? (
                  <div className="flex items-center justify-between bg-green-50 rounded-lg p-2">
                    <button
                      onClick={() => removeFromCart(product.id)}
                      className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="font-medium text-green-600">{cartItems[product.id]}</span>
                    <button
                      onClick={() => addToCart(product.id)}
                      className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => addToCart(product.id)}
                    className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
                  >
                    أضف للسلة
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* زر السلة العائم */}
      {Object.keys(cartItems).length > 0 && (
        <button
          onClick={() => navigate('/cart')}
          className="fixed bottom-20 left-4 right-4 bg-green-500 text-white py-4 rounded-lg font-medium shadow-lg flex items-center justify-center"
        >
          <i className="ri-shopping-cart-fill ml-2"></i>
          عرض السلة ({Object.values(cartItems).reduce((a, b) => a + b, 0)} عنصر)
        </button>
      )}

      {/* شريط التنقل السفلي */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="grid grid-cols-4 h-16">
          <button 
            onClick={() => navigate('/home')}
            className="flex flex-col items-center justify-center text-gray-400"
          >
            <i className="ri-home-line text-xl mb-1"></i>
            <span className="text-xs">الرئيسية</span>
          </button>
          <button className="flex flex-col items-center justify-center text-gray-400">
            <i className="ri-search-line text-xl mb-1"></i>
            <span className="text-xs">البحث</span>
          </button>
          <button 
            onClick={() => navigate('/cart')}
            className="flex flex-col items-center justify-center text-green-500 relative"
          >
            <i className="ri-shopping-cart-fill text-xl mb-1"></i>
            <span className="text-xs">السلة</span>
            {Object.keys(cartItems).length > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">{Object.values(cartItems).reduce((a, b) => a + b, 0)}</span>
              </div>
            )}
          </button>
          <button className="flex flex-col items-center justify-center text-gray-400">
            <i className="ri-file-list-line text-xl mb-1"></i>
            <span className="text-xs">طلباتي</span>
          </button>
        </div>
      </div>
    </div>
  );
}
